import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { flatQuestions, typeMeta } from "@/lib/forms";
import { ArrowDown, Flag } from "lucide-react";

const OPS = { is: "is", is_not: "is not", contains: "contains", gt: ">", gte: "≥", lt: "<", lte: "≤", answered: "answered", not_answered: "empty" };

function ruleLabel(r, qs) {
  const parts = (r.conditions || []).map((c) => {
    const q = c.ref === "__score" ? { label: "Score" } : qs.find((x) => x.id === c.ref) || {};
    return `${(q.label || c.ref || "").slice(0, 28)} ${OPS[c.op] || c.op}${["answered", "not_answered"].includes(c.op) ? "" : ` ${c.value ?? ""}`}`;
  });
  return parts.join(r.match === "any" ? " or " : " & ") || "Always";
}

function useEdges(blocks, endings) {
  return useMemo(() => {
    const qs = flatQuestions(blocks);
    const out = [];
    blocks.forEach((b, i) => {
      const lg = b.logic || {};
      (lg.rules || []).forEach((r, k) => { if (r.to) out.push({ id: `${b.id}-r${k}`, from: b.id, to: r.to, label: ruleLabel(r, qs), kind: "rule" }); });
      if (lg.otherwise) out.push({ id: `${b.id}-else`, from: b.id, to: lg.otherwise, label: "Otherwise", kind: "else" });
      else if (i === blocks.length - 1 && endings[0]) out.push({ id: `${b.id}-end`, from: b.id, to: endings[0].id, label: "Next", kind: "else" });
    });
    return out;
  }, [blocks, endings]);
}

function Card({ b, i, sel, onSelect, setRef }) {
  const meta = typeMeta(b.type) || {};
  const Icon = meta.icon;
  const rules = (b.logic?.rules || []).length;
  return (
    <button ref={setRef} onClick={() => onSelect(b.id)} data-testid={`map-block-${b.id}`}
      className={`w-full text-left panel px-3 py-2 transition-colors ${sel === b.id ? "border-white" : "hover:border-neutral-500"}`}>
      <div className="flex items-center gap-2 text-[11px] text-neutral-500">{Icon && <Icon size={12} />}<span>{i + 1}</span><span>{meta.label || b.type}</span>
        {rules > 0 && <span className="ml-auto pill text-[10px]">{rules} rule{rules > 1 ? "s" : ""}</span>}</div>
      <div className="text-sm leading-snug break-words mt-0.5">{b.label || "(untitled)"}</div>
    </button>
  );
}

export default function LogicMap({ blocks, endings, sel, onSelect }) {
  const wrap = useRef(null);
  const refs = useRef({});
  const edges = useEdges(blocks, endings);
  const [geo, setGeo] = useState({ paths: [], h: 0, w: 0 });
  const endIds = new Set(endings.map((e) => e.id));

  useLayoutEffect(() => {
    const calc = () => {
      if (!wrap.current) return;
      const W = wrap.current.getBoundingClientRect();
      const box = (id) => { const el = refs.current[id]; if (!el) return null; const r = el.getBoundingClientRect(); return { l: r.left - W.left, r: r.right - W.left, t: r.top - W.top, b: r.bottom - W.top, y: r.top - W.top + r.height / 2 }; };
      let lane = 0;
      const paths = edges.map((e) => {
        const a = box(e.from); const z = box(e.to);
        if (!a || !z) return null;
        if (endIds.has(e.to)) {
          const mx = (a.r + z.l) / 2;
          return { ...e, d: `M${a.r},${a.y} C${mx},${a.y} ${mx},${z.y} ${z.l - 4},${z.y}`, lx: a.r + (z.l - a.r) * 0.3, ly: a.y + (z.y - a.y) * 0.12 };
        }
        const x = a.r + 18 + (lane++ % 6) * 14;
        return { ...e, d: `M${a.r},${a.y} H${x} V${z.y} H${z.r + 4}`, lx: x, ly: (a.y + z.y) / 2 };
      }).filter(Boolean);
      setGeo({ paths, h: W.height, w: W.width });
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [edges]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone = (e) => (endings.find((x) => x.id === e.to)?.outcome === "disqualified" || endings.find((x) => x.id === e.to)?.route === "dq" ? "#f87171" : e.kind === "else" ? "#737373" : "#e5e5e5");
  return (
    <div className="panel p-4 overflow-x-auto" data-testid="logic-map">
      <div className="text-[11px] text-neutral-500 mb-3">Each block runs its rules in order after it's answered. Click a block or a label to edit its rules on the right. Red arrows go to disqualified / DQ endings.</div>
      <div ref={wrap} className="relative grid grid-cols-[minmax(240px,340px)_minmax(160px,1fr)_minmax(200px,260px)] min-w-[720px]">
        <div className="space-y-0">
          {blocks.map((b, i) => (
            <div key={b.id}>
              <Card b={b} i={i} sel={sel} onSelect={onSelect} setRef={(el) => { refs.current[b.id] = el; }} />
              {i < blocks.length - 1 && <div className={`flex justify-center py-1 ${b.logic?.otherwise ? "opacity-20" : "text-neutral-600"}`}><ArrowDown size={14} /></div>}
            </div>
          ))}
        </div>
        <div />
        <div className="space-y-3 self-center">
          {endings.map((e) => (
            <button key={e.id} ref={(el) => { refs.current[e.id] = el; }} onClick={() => onSelect(e.id)} data-testid={`map-ending-${e.id}`}
              className={`w-full text-left panel px-3 py-2 ${sel === e.id ? "border-white" : ""} ${e.outcome === "disqualified" || e.route === "dq" ? "border-red-500/50" : ""}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-neutral-500"><Flag size={11} />Ending · {e.outcome || "neutral"}{e.route ? ` · ${e.route.replace("sop:", "SOP ")}` : ""}</div>
              <div className="text-sm break-words">{e.title || e.id}</div>
            </button>
          ))}
        </div>
        <svg className="absolute inset-0 pointer-events-none" width={geo.w} height={geo.h}>
          <defs>{["#e5e5e5", "#737373", "#f87171"].map((c) => (
            <marker key={c} id={`ah${c.slice(1)}`} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={c} /></marker>))}</defs>
          {geo.paths.map((p) => (
            <path key={p.id} d={p.d} fill="none" stroke={tone(p)} strokeWidth={sel === p.from ? 2 : 1.2} strokeDasharray={p.kind === "else" ? "4 4" : undefined}
              markerEnd={`url(#ah${tone(p).slice(1)})`} opacity={sel && sel !== p.from ? 0.35 : 1} />
          ))}
        </svg>
        {geo.paths.map((p) => (
          <button key={`l-${p.id}`} onClick={() => onSelect(p.from)} title={p.label} data-testid={`map-edge-${p.id}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 max-w-[170px] px-1.5 py-0.5 rounded bg-[var(--panel)] border border-[var(--border)] text-[10px] leading-tight text-neutral-300 text-left break-words hover:border-white"
            style={{ left: p.lx, top: p.ly }}>{p.label}</button>
        ))}
      </div>
    </div>
  );
}
